/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { getActiveLocale } from '@/ux-common/src/utils/locale';
import { toast } from 'sonner';

export const formatDateTime = (dateString: string, locale?: string) => {
  const date = new Date(dateString);
  // return empty string if date is invalid
  if (isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  const activeLocale = getActiveLocale(locale);
  return date.toLocaleString(activeLocale, options).replace(',', '');
};

/**
 * Copies the given trace ID to the clipboard.
 * @param traceId - The trace ID to be copied.
 */
export const copyTraceId = (
  traceId: string,
  copyMessage: string | undefined
) => {
  if (traceId) {
    navigator.clipboard.writeText(traceId);
    toast.success(`${copyMessage}`);
  }
};

/**
 * Copies the given fact ID to the clipboard.
 * @param factId - The fact ID to be copied.
 */
export const copyFactId = (factId: string, copyMessage: string | undefined) => {
  if (factId) {
    navigator.clipboard.writeText(factId);
    toast.success(`${copyMessage || 'Fact ID copied to clipboard'}`);
  }
};

/**
 * Truncates the trace ID if it exceeds a certain length.
 * @param traceId - The trace ID to truncate.
 * @param length - The maximum length of the trace ID before truncation. Default is 15.
 * @returns Truncated trace ID.
 */
export const truncateTraceId = (traceId: string, length = 15): string => {
  if (!traceId) return '';
  if (traceId.length <= length) return traceId;
  return `${traceId.slice(0, 5)}...${traceId.slice(-5)}`;
};

export const formatDateToTodayTime = (dateString?: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfTargetDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const dayDiff = Math.floor(
    (startOfToday.getTime() - startOfTargetDate.getTime()) / 86400000
  );

  const timeParts = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);

  const hour = timeParts.find((part) => part.type === 'hour')?.value || '00';
  const minute =
    timeParts.find((part) => part.type === 'minute')?.value || '00';
  const second =
    timeParts.find((part) => part.type === 'second')?.value || '00';
  const dayPeriod =
    timeParts.find((part) => part.type === 'dayPeriod')?.value?.toUpperCase() ||
    '';
  const time = `${hour}.${minute}.${second}${dayPeriod ? ` ${dayPeriod}` : ''}`;

  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  if (dayDiff > 1 && dayDiff <= 6) return `${dayDiff}d, ${time}`;

  const localDate = date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return `${localDate}, ${time}`;
};

/**
 * Format a duration in milliseconds into a human-readable string.
 * Examples: 850 ms, 34 s, 2m 15s, 1h 5m 3s, 1d 5h, 3d 5h 30m
 */
export const formatDuration = (ms: number): string => {
  // Show milliseconds up to 1,000 ms, else split into units (h, m, s, ms)
  if (ms < 1000) return `${ms}ms`;

  const totalSeconds = Math.floor(ms / 1000);
  const milliseconds = ms % 1000;
  // if totalSeconds is less than 60, return duration in seconds and milliseconds
  if (totalSeconds < 60) {
    return milliseconds
      ? `${totalSeconds}s ${milliseconds}ms`
      : `${totalSeconds}s`;
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  // if totalMinutes is less than 60, return duration in minutes, seconds and milliseconds
  if (totalMinutes < 60) {
    const parts = [`${totalMinutes}m`];
    if (seconds) parts.push(`${seconds}s`);
    if (milliseconds) parts.push(`${milliseconds}ms`);
    return parts.join(' ');
  }
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  // if totalHours is less than 24, return duration in hours, minutes, seconds and milliseconds
  if (totalHours < 24) {
    const parts = [`${totalHours}h`];
    if (minutes) parts.push(`${minutes}m`);
    if (seconds) parts.push(`${seconds}s`);
    if (milliseconds) parts.push(`${milliseconds}ms`);
    return parts.join(' ');
  }

  // show duration in days, hours, minutes, seconds and milliseconds
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const parts = [`${days}d`];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds) parts.push(`${seconds}s`);
  if (milliseconds) parts.push(`${milliseconds}ms`);
  return parts.join(' ');
};
